import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { REACT_QUERY_CONFIG } from "@/lib/constants";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

// Public pages - load immediately
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

// Protected pages - lazy load for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Schedule = lazy(() => import("./pages/Schedule"));
const Users = lazy(() => import("./pages/Users"));
const StreamView = lazy(() => import("./pages/StreamView"));
const Streams = lazy(() => import("./pages/Streams"));
const ChatArchives = lazy(() => import("./pages/ChatArchives"));
const Settings = lazy(() => import("./pages/Settings"));
const AuctionPosts = lazy(() => import("./pages/AuctionPosts"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Configure React Query with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: REACT_QUERY_CONFIG,
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Staff-only routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute requireRoles={["admin", "auctioneer", "clerk"]}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/schedule"
                  element={
                    <ProtectedRoute requireRoles={["admin", "auctioneer", "clerk"]}>
                      <Schedule />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute requireRoles={["admin", "auctioneer", "clerk"]}>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/chat-archives"
                  element={
                    <ProtectedRoute requireRoles={["admin", "auctioneer", "clerk"]}>
                      <ChatArchives />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute requireRoles={["admin"]}>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/auction-posts"
                  element={
                    <ProtectedRoute requireRoles={["admin", "auctioneer", "clerk"]}>
                      <AuctionPosts />
                    </ProtectedRoute>
                  }
                />

                {/* Authenticated routes */}
                <Route
                  path="/streams"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <Streams />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stream/:id"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <StreamView />
                    </ProtectedRoute>
                  }
                />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
